import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, CATEGORY_LABEL } from "@/lib/api";
import { PageHeader, Table, Btn, Card, Modal, Input, Select, Badge, Textarea } from "@/components/UI";
import { Plus, Pencil, Trash2, ArrowLeft, Factory, ClipboardList, FlaskConical, Tag, Send, KeyRound, Eye, Download, Printer } from "lucide-react";
import { previewPdf, downloadPdf, printPdf } from "@/lib/pdfActions";
import { usePagination, ListPagination } from "@/components/ListPagination";
import ProductCardModal from "@/components/ProductCardModal";
import { CustomerDocuments } from "@/components/CustomerDocuments";

/* Reusable Actions column renderer */
function actionCol(onEdit, onDelete, testidPrefix) {
  return {
    label: "İşlem",
    render: (r) => (
      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
        <Btn size="sm" variant="secondary" onClick={() => onEdit(r)} data-testid={`edit-${testidPrefix}-${r.id}`}><Pencil size={12}/> Düzenle</Btn>
        <Btn size="sm" variant="danger" onClick={() => onDelete(r)} data-testid={`delete-${testidPrefix}-${r.id}`}><Trash2 size={12}/> Sil</Btn>
      </div>
    ),
  };
}

async function confirmAndDelete(path, label, load) {
  if (!window.confirm(`${label} kaydı arşivlensin/silinsin mi?\n\nBağımlılığı olan kayıtlar pasife alınır, olmayanlar tamamen silinir. Bu işlem denetim kaydına yazılır.`)) return;
  try {
    await api.del(path);
    await load();
  } catch (e) {
    alert(e.response?.data?.detail || "Silme başarısız");
  }
}

/* ---------- Customers ---------- */
export function CustomersPage() {
  const [rows, setRows] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { legal_name: "", tax_office: "", tax_id: "", contact_name: "", phone: "", email: "", billing_address: "", notes: "", active: true, delivery_addresses: [] };
  const [f, setF] = useState(empty);
  const [newBrand, setNewBrand] = useState("");
  const [initialBrand, setInitialBrand] = useState("");
  const load = () => Promise.all([api.get("/customers"), api.get("/brands")]).then(([c, b]) => { setRows(c); setBrands(b); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    let saved;
    if (edit) saved = await api.put(`/customers/${edit.id}`, f);
    else saved = await api.post("/customers", f);
    // Yeni müşteride girilen ilk marka varsa otomatik oluştur
    if (!edit && initialBrand.trim() && saved?.id) {
      try { await api.post("/brands", { customer_id: saved.id, name: initialBrand.trim(), active: true }); }
      catch (e) { alert(`Müşteri kaydedildi ama marka oluşturulamadı: ${e.response?.data?.detail || "hata"}`); }
    }
    setOpen(false); setEdit(null); setF(empty); setInitialBrand(""); load();
  };
  const openNew = () => { setEdit(null); setF(empty); setNewBrand(""); setInitialBrand(""); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ ...empty, ...r }); setNewBrand(""); setInitialBrand(""); setOpen(true); };
  const del = (r) => confirmAndDelete(`/customers/${r.id}`, r.legal_name, load);
  const custBrands = edit ? brands.filter(b => b.customer_id === edit.id) : [];
  const nav = useNavigate();
  const addBrand = async () => {
    if (!edit || !newBrand.trim()) return;
    await api.post("/brands", { customer_id: edit.id, name: newBrand.trim(), active: true });
    setNewBrand("");
    load();
  };
  const removeBrand = async (b) => {
    if (!window.confirm(`${b.name} markası silinsin mi?`)) return;
    await api.del(`/brands/${b.id}`);
    load();
  };
  const pg = usePagination(rows, "customers", 100);
  return (
    <div>
      <PageHeader title="Müşteriler" description="Marka sahibi müşteri kayıtları — markalar müşteri düzenleme ekranından eklenir" actions={
        <div className="flex items-center gap-2">
          {/* Iter 202 — Kontrollü Müşteri Listesi PDF (L15) icon-only trio. */}
          <div className="inline-flex items-center gap-1 border rounded-md px-1.5 py-1 bg-slate-50" title="L15 Müşteri Listesi Kontrollü PDF">
            <span className="text-[10px] font-semibold text-slate-600 mr-1">L15:</span>
            <button title="Önizle" data-testid="cust-l15-preview"
              onClick={() => previewPdf("/api/customers/master-list/pdf", "L15_Musteri_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-indigo-600 hover:bg-indigo-700 text-white">
              <Eye size={14}/>
            </button>
            <button title="İndir" data-testid="cust-l15-download"
              onClick={() => downloadPdf("/api/customers/master-list/pdf", "L15_Musteri_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
              <Download size={14}/>
            </button>
            <button title="Yazdır" data-testid="cust-l15-print"
              onClick={() => printPdf("/api/customers/master-list/pdf", "L15_Musteri_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
              <Printer size={14}/>
            </button>
          </div>
          <Btn onClick={openNew} data-testid="new-customer"><Plus size={14}/> Yeni Müşteri</Btn>
        </div>
      } />
      <Table testid="customers-table"
        onRowClick={(r) => nav(`/customers/${r.id}`)}
        columns={[
          { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Firma", key: "legal_name" },
          { label: "Vergi No", key: "tax_id" },
          { label: "İletişim", key: "contact_name" },
          { label: "Telefon", key: "phone" },
          { label: "Markalar", render: r => {
            const bs = brands.filter(b => b.customer_id === r.id);
            if (bs.length === 0) return <span className="text-slate-400 text-xs">—</span>;
            return <div className="flex flex-wrap gap-1">{bs.slice(0, 3).map(b => <span key={b.id} className="inline-block px-1.5 py-0.5 rounded bg-fuchsia-50 text-fuchsia-700 border border-fuchsia-200 text-[10px]">{b.name}</span>)}{bs.length > 3 && <span className="text-[10px] text-slate-500">+{bs.length - 3}</span>}</div>;
          } },
          { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
          actionCol(openEdit, del, "cust"),
        ]}
        rows={pg.paged} />
      <ListPagination pagination={pg} testid="customers-pagination"/>
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Müşteri Düzenle" : "Yeni Müşteri"} size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Input label="Firma Ünvanı *" value={f.legal_name} onChange={e => setF({ ...f, legal_name: e.target.value })} data-testid="cust-name" />
          <Input label="Vergi Dairesi" value={f.tax_office} onChange={e => setF({ ...f, tax_office: e.target.value })} />
          <Input label="Vergi No" value={f.tax_id} onChange={e => setF({ ...f, tax_id: e.target.value })} />
          <Input label="Yetkili Kişi" value={f.contact_name} onChange={e => setF({ ...f, contact_name: e.target.value })} />
          <Input label="Telefon" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} />
          <Input label="E-posta" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} />
          <div className="col-span-2"><Textarea label="Fatura Adresi" value={f.billing_address} onChange={e => setF({ ...f, billing_address: e.target.value })} /></div>
          <div className="col-span-2"><Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })}>
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
        </div>
        {edit && (
          <div className="mt-4 p-3 rounded-lg bg-fuchsia-50/50 border border-fuchsia-200/60">
            <div className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-2">Markalar</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {custBrands.length === 0 && <span className="text-xs text-slate-500">Henüz marka yok.</span>}
              {custBrands.map(b => (
                <span key={b.id} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-fuchsia-300 text-xs">
                  {b.name}
                  <button onClick={() => removeBrand(b)} className="text-red-500 hover:text-red-700 text-sm leading-none" data-testid={`cust-brand-rm-${b.id}`}>×</button>
                </span>
              ))}
            </div>
            <div className="flex gap-2">
              <input placeholder="Yeni marka adı" value={newBrand} onChange={e => setNewBrand(e.target.value)} className="flex-1 px-2 py-1 text-sm border rounded-md bg-white" data-testid="cust-brand-name"/>
              <Btn size="sm" onClick={addBrand} data-testid="cust-brand-add"><Plus size={12}/> Marka Ekle</Btn>
            </div>
          </div>
        )}
        {!edit && (
          <div className="mt-3 p-3 rounded-lg bg-fuchsia-50/50 border border-fuchsia-200/60">
            <div className="text-xs font-semibold uppercase tracking-wider text-fuchsia-700 mb-2">İlk Marka (opsiyonel — kaydedildiğinde otomatik oluşturulur)</div>
            <Input label="Marka Adı" value={initialBrand} onChange={e => setInitialBrand(e.target.value)} data-testid="cust-first-brand" placeholder="örn. Beauty XYZ"/>
            <div className="text-[10px] text-slate-500 mt-1 italic">💡 Müşteri kaydedildikten sonra düzenle ekranından ek markalar da ekleyebilirsiniz.</div>
          </div>
        )}
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-customer">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* ---------- Brands ---------- */
export function BrandsPage() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { customer_id: "", name: "", notes: "", active: true };
  const [f, setF] = useState(empty);
  const load = () => Promise.all([api.get("/brands"), api.get("/customers")]).then(([b, c]) => { setRows(b); setCustomers(c); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (edit) await api.put(`/brands/${edit.id}`, f);
    else await api.post("/brands", f);
    setOpen(false); setEdit(null); setF(empty); load();
  };
  const openNew = () => { setEdit(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ ...empty, ...r }); setOpen(true); };
  const del = (r) => confirmAndDelete(`/brands/${r.id}`, r.name, load);
  const custName = (id) => customers.find(c => c.id === id)?.legal_name || "—";
  return (
    <div>
      <PageHeader title="Markalar" description="Müşteriye bağlı marka kayıtları" actions={<Btn onClick={openNew} data-testid="new-brand"><Plus size={14}/> Yeni Marka</Btn>} />
      <Table columns={[
        { label: "Marka", key: "name" },
        { label: "Müşteri", render: r => custName(r.customer_id) },
        { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
        actionCol(openEdit, del, "brand"),
      ]} rows={rows} />
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Marka Düzenle" : "Yeni Marka"}>
        <div className="space-y-3">
          <Select label="Müşteri *" value={f.customer_id} onChange={e => setF({ ...f, customer_id: e.target.value })}>
            <option value="">Seçiniz...</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
          </Select>
          <Input label="Marka Adı *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="brand-name" />
          <Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} />
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })}>
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-brand">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* ---------- Products ---------- */
export function ProductsPage() {
  const [rows, setRows] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [brands, setBrands] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { customer_id: "", brand_id: "", name: "", category: "", variant: "", fill_qty: 50, fill_unit: "ml", units_per_carton: 24, shelf_life_days: 730, status: "active" };
  const [f, setF] = useState(empty);
  const load = () => Promise.all([api.get("/products"), api.get("/customers"), api.get("/brands")]).then(([p, c, b]) => { setRows(p); setCustomers(c); setBrands(b); });
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (edit) await api.put(`/products/${edit.id}`, f);
    else await api.post("/products", f);
    setOpen(false); setEdit(null); setF(empty); load();
  };
  const openNew = () => { setEdit(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ ...empty, ...r }); setOpen(true); };
  const del = (r) => confirmAndDelete(`/products/${r.id}`, r.name, load);
  const filteredBrands = brands.filter(b => !f.customer_id || b.customer_id === f.customer_id);
  const cname = id => customers.find(c => c.id === id)?.legal_name || "—";
  const bname = id => brands.find(b => b.id === id)?.name || "—";
  return (
    <div>
      <PageHeader title="Ürünler" description="Bitmiş ürün / SKU kayıtları" actions={<Btn onClick={openNew} data-testid="new-product"><Plus size={14}/> Yeni Ürün</Btn>} />
      <Table columns={[
        { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
        { label: "Ürün", key: "name" },
        { label: "Müşteri", render: r => cname(r.customer_id) },
        { label: "Marka", render: r => bname(r.brand_id) },
        { label: "Dolum", render: r => `${r.fill_qty} ${r.fill_unit}` },
        { label: "Karton/adet", render: r => r.units_per_carton },
        { label: "Durum", render: r => <Badge level={r.status === "active" ? "ok" : "inactive"}>{r.status === "active" ? "Aktif" : "Pasif"}</Badge> },
        actionCol(openEdit, del, "prod"),
      ]} rows={rows} />
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Ürün Düzenle" : "Yeni Ürün"} size="lg">
        <div className="grid grid-cols-2 gap-3">
          <Select label="Müşteri *" value={f.customer_id} onChange={e => setF({ ...f, customer_id: e.target.value, brand_id: "" })}>
            <option value="">Seçiniz...</option>
            {customers.map(c => <option key={c.id} value={c.id}>{c.legal_name}</option>)}
          </Select>
          <Select label="Marka *" value={f.brand_id} onChange={e => setF({ ...f, brand_id: e.target.value })}>
            <option value="">Seçiniz...</option>
            {filteredBrands.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </Select>
          <div className="col-span-2"><Input label="Ürün Adı *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="prod-name" /></div>
          <Input label="Kategori" value={f.category} onChange={e => setF({ ...f, category: e.target.value })} />
          <Input label="Varyant" value={f.variant} onChange={e => setF({ ...f, variant: e.target.value })} />
          <Input label="Dolum Miktarı" type="number" value={f.fill_qty} onChange={e => setF({ ...f, fill_qty: parseFloat(e.target.value) })} />
          <Select label="Dolum Birimi" value={f.fill_unit} onChange={e => setF({ ...f, fill_unit: e.target.value })}>
            <option value="ml">ml</option><option value="g">g</option><option value="adet">adet</option>
          </Select>
          <Input label="Karton/Adet" type="number" value={f.units_per_carton} onChange={e => setF({ ...f, units_per_carton: parseInt(e.target.value) })} />
          <Input label="Raf Ömrü (gün)" type="number" value={f.shelf_life_days} onChange={e => setF({ ...f, shelf_life_days: parseInt(e.target.value) })} />
          <Select label="Durum" value={f.status} onChange={e => setF({ ...f, status: e.target.value })}>
            <option value="active">Aktif</option><option value="inactive">Pasif</option>
          </Select>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-product">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* ---------- Customer Detail (mini profile) ---------- */
export function CustomerDetailPage() {
  const { id } = useParams();
  const nav = useNavigate();
  const [customer, setCustomer] = useState(null);
  const [brands, setBrands] = useState([]);
  const [formulas, setFormulas] = useState([]);
  const [orders, setOrders] = useState([]);
  const [products, setProducts] = useState([]);
  // iter280 — Ürün kart modalı state (Formüller alanında ürün adına tıklayınca açılır)
  const [productCardId, setProductCardId] = useState(null);
  useEffect(() => {
    Promise.all([
      api.get(`/customers/${id}`),
      api.get("/brands"),
      api.get("/formulas"),
      api.get("/sales-orders"),
      api.get("/products"),
    ]).then(([c, b, f, s, p]) => {
      setCustomer(c);
      setBrands(b.filter(x => x.customer_id === id));
      setFormulas(f.filter(x => x.customer_id === id));
      setOrders(s.filter(x => x.customer_id === id));
      setProducts(p.filter(x => x.customer_id === id));
    });
  }, [id]);
  if (!customer) return <div className="p-6 text-slate-500 dark:text-slate-400">Yükleniyor...</div>;
  const openOrders = orders.filter(o => !["delivered", "cancelled"].includes(o.status));
  const pname = pid => products.find(p => p.id === pid)?.name || "—";
  return (
    <div>
      <PageHeader
        title={customer.legal_name}
        description={<span className="font-mono text-xs">{customer.code} · {customer.tax_id || "—"}</span>}
        actions={<Btn variant="secondary" onClick={() => nav("/customers")} data-testid="cust-back"><ArrowLeft size={14}/> Listeye Dön</Btn>}
      />
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <Card className="p-4 bg-gradient-to-br from-fuchsia-50 to-pink-50 dark:from-fuchsia-900/20 dark:to-pink-900/20 border-fuchsia-200/60 dark:border-fuchsia-500/30">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-fuchsia-700 dark:text-fuchsia-300 mb-1"><Tag size={12}/> Markalar</div>
          <div className="text-3xl font-bold text-fuchsia-800 dark:text-fuchsia-200" style={{ fontFamily: "'Manrope', sans-serif" }}>{brands.length}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-violet-50 to-indigo-50 dark:from-violet-900/20 dark:to-indigo-900/20 border-violet-200/60 dark:border-violet-500/30">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-violet-700 dark:text-violet-300 mb-1"><FlaskConical size={12}/> Formüller</div>
          <div className="text-3xl font-bold text-violet-800 dark:text-violet-200" style={{ fontFamily: "'Manrope', sans-serif" }}>{formulas.length}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20 border-emerald-200/60 dark:border-emerald-500/30">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-700 dark:text-emerald-300 mb-1"><ClipboardList size={12}/> Açık Siparişler</div>
          <div className="text-3xl font-bold text-emerald-800 dark:text-emerald-200" style={{ fontFamily: "'Manrope', sans-serif" }}>{openOrders.length}</div>
          <div className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">Toplam sipariş: {orders.length}</div>
        </Card>
        <Card className="p-4 bg-gradient-to-br from-sky-50 to-cyan-50 dark:from-sky-900/20 dark:to-cyan-900/20 border-sky-200/60 dark:border-sky-500/30">
          <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-sky-700 dark:text-sky-300 mb-1"><Factory size={12}/> Ürünler</div>
          <div className="text-3xl font-bold text-sky-800 dark:text-sky-200" style={{ fontFamily: "'Manrope', sans-serif" }}>{products.length}</div>
        </Card>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <Card className="p-4">
          <div className="text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>İletişim</div>
          <div className="space-y-1.5 text-xs">
            <div><span className="text-slate-500 dark:text-slate-400 uppercase tracking-wide text-[10px] block">Yetkili</span>{customer.contact_name || "—"}</div>
            <div><span className="text-slate-500 dark:text-slate-400 uppercase tracking-wide text-[10px] block">Telefon</span>{customer.phone || "—"}</div>
            <div><span className="text-slate-500 dark:text-slate-400 uppercase tracking-wide text-[10px] block">E-posta</span>{customer.email || "—"}</div>
            <div><span className="text-slate-500 dark:text-slate-400 uppercase tracking-wide text-[10px] block">Fatura Adresi</span>{customer.billing_address || "—"}</div>
          </div>
        </Card>
        <Card className="p-4 lg:col-span-2">
          <div className="text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Markalar ({brands.length})</div>
          {brands.length === 0 ? <div className="text-xs text-slate-400">Marka yok</div> :
            <div className="flex flex-wrap gap-2">
              {brands.map(b => <span key={b.id} className="inline-flex items-center px-2.5 py-1 rounded-md bg-fuchsia-50 dark:bg-fuchsia-500/10 text-fuchsia-700 dark:text-fuchsia-300 border border-fuchsia-200 dark:border-fuchsia-500/30 text-xs font-medium">{b.name}</span>)}
            </div>}
        </Card>
      </div>
      <CustomerDocuments customerId={id}/>
      <Card className="p-4 mb-4">
        <div className="text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Formüller</div>
        <Table columns={[
          { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
          { label: "Formül", key: "name" },
          { label: "Ürün", render: r => r.product_id ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setProductCardId(r.product_id); }}
              className="text-slate-700 dark:text-slate-200 hover:text-indigo-700 hover:underline decoration-dotted text-left"
              title="Ürün kartını aç — formüller + üretim + teslimat özeti"
              data-testid={`cust-formula-prod-${r.id}`}
            >{pname(r.product_id)}</button>
          ) : (pname(r.product_id)) },
          { label: "Versiyon", key: "version" },
          { label: "Satış (EUR)", render: r => (r.sales_price_eur || 0).toFixed(2) },
          { label: "Durum", render: r => <Badge level={r.status === "approved" ? "ok" : (r.status === "draft" ? "attention" : "inactive")}>{r.status}</Badge> },
        ]} rows={formulas} empty="Formül yok"/>
      </Card>
      {/* iter280 — Ürün Kart Modalı (Müşteri detay → Formüller ürün adı tıklaması) */}
      <ProductCardModal productId={productCardId} onClose={() => setProductCardId(null)}/>
      <Card className="p-4">
        <div className="text-sm font-semibold mb-2 text-slate-800 dark:text-slate-100" style={{ fontFamily: "'Manrope', sans-serif" }}>Açık Siparişler ({openOrders.length})</div>
        <Table columns={[
          { label: "Sipariş No", render: r => <Link to={`/sales-orders/${r.id}`} className="font-mono text-xs text-indigo-600 dark:text-indigo-400 hover:underline">{r.order_no}</Link> },
          { label: "PO", key: "customer_po" },
          { label: "Teslim", render: r => (r.required_delivery || "").slice(0, 10) },
          { label: "Kalem", render: r => r.lines?.length || 0 },
          { label: "Durum", render: r => <Badge level={r.status === "approved" ? "ok" : "attention"}>{r.status}</Badge> },
        ]} rows={openOrders} onRowClick={(r) => nav(`/sales-orders/${r.id}`)} empty="Açık sipariş yok"/>
      </Card>
    </div>
  );
}

/* ---------- Materials ---------- */
export function MaterialsPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { name: "", category: "raw_material", unit: "kg", min_stock: 0, critical_stock: 0, target_stock: 0, lead_time_days: 7, unit_cost: 0, currency: "TRY", vat_rate: 20, active: true };
  const [f, setF] = useState(empty);
  const load = () => api.get("/materials").then(setRows);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (edit) await api.put(`/materials/${edit.id}`, f);
    else await api.post("/materials", f);
    setOpen(false); setEdit(null); setF(empty); load();
  };
  const openNew = () => { setEdit(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ ...empty, ...r }); setOpen(true); };
  const del = (r) => confirmAndDelete(`/materials/${r.id}`, r.name, load);
  return (
    <div>
      <PageHeader title="Malzemeler" description="Ham madde, ambalaj ve diğer stok kalemleri" actions={<Btn onClick={openNew} data-testid="new-material"><Plus size={14}/> Yeni Malzeme</Btn>} />
      <Table columns={[
        { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
        { label: "Malzeme", key: "name" },
        { label: "Kategori", render: r => CATEGORY_LABEL[r.category] || r.category },
        { label: "Birim", key: "unit" },
        { label: "Min", key: "min_stock" },
        { label: "Kritik", key: "critical_stock" },
        { label: "Tedarik Süresi", render: r => `${r.lead_time_days} gün` },
        { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
        actionCol(openEdit, del, "mat"),
      ]} rows={rows} />
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Malzeme Düzenle" : "Yeni Malzeme"} size="lg">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Malzeme Adı *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="mat-name" /></div>
          <Select label="Kategori" value={f.category} onChange={e => setF({ ...f, category: e.target.value })}>
            {Object.entries(CATEGORY_LABEL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Select label="Birim" value={f.unit} onChange={e => setF({ ...f, unit: e.target.value })}>
            <option value="kg">kg</option><option value="g">g</option><option value="l">l</option><option value="ml">ml</option><option value="adet">adet</option><option value="box">kutu</option><option value="carton">karton</option><option value="roll">rulo</option>
          </Select>
          <Input label="Min. Stok" type="number" value={f.min_stock} onChange={e => setF({ ...f, min_stock: parseFloat(e.target.value) })} />
          <Input label="Kritik Stok" type="number" value={f.critical_stock} onChange={e => setF({ ...f, critical_stock: parseFloat(e.target.value) })} />
          <Input label="Hedef Stok" type="number" value={f.target_stock} onChange={e => setF({ ...f, target_stock: parseFloat(e.target.value) })} />
          <Input label="Tedarik Süresi (gün)" type="number" value={f.lead_time_days} onChange={e => setF({ ...f, lead_time_days: parseInt(e.target.value) })} />
          <Input label="Birim Maliyet" type="number" step="0.01" value={f.unit_cost} onChange={e => setF({ ...f, unit_cost: parseFloat(e.target.value) })} />
          <Select label="Para Birimi" value={f.currency} onChange={e => setF({ ...f, currency: e.target.value })}>
            <option>TRY</option><option>USD</option><option>EUR</option>
          </Select>
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })}>
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-material">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* ---------- Warehouses ---------- */
export function WarehousesPage() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { name: "", type: "raw", address: "", active: true };
  const [f, setF] = useState(empty);
  const load = () => api.get("/warehouses").then(setRows);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (edit) await api.put(`/warehouses/${edit.id}`, f);
    else await api.post("/warehouses", f);
    setOpen(false); setEdit(null); setF(empty); load();
  };
  const openNew = () => { setEdit(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ ...empty, ...r }); setOpen(true); };
  const del = (r) => confirmAndDelete(`/warehouses/${r.id}`, r.name, load);
  const typeLabels = { raw: "Ham Madde", packaging: "Ambalaj", quarantine: "Karantina", rejected: "Red", semi: "Yarı Mamül", finished: "Bitmiş Ürün", shipment: "Sevkiyat" };
  return (
    <div>
      <PageHeader title="Depolar" actions={<Btn onClick={openNew} data-testid="new-warehouse"><Plus size={14}/> Yeni Depo</Btn>} />
      <Table columns={[
        { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
        { label: "Depo", key: "name" },
        { label: "Tip", render: r => typeLabels[r.type] || r.type },
        { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
        actionCol(openEdit, del, "wh"),
      ]} rows={rows} />
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Depo Düzenle" : "Yeni Depo"}>
        <div className="space-y-3">
          <Input label="Depo Adı" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="wh-name"/>
          <Select label="Tip" value={f.type} onChange={e => setF({ ...f, type: e.target.value })}>
            {Object.entries(typeLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
          </Select>
          <Textarea label="Adres" value={f.address} onChange={e => setF({ ...f, address: e.target.value })} />
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })}>
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
        </div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-warehouse">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}


/* ---------- Suppliers ---------- */
export function SuppliersPage() {
  const [tab, setTab] = useState("list");
  return (
    <div>
      <PageHeader title="Tedarikçiler" description="Hammadde ve ambalaj tedarikçileri · Kalite performans skorları"/>
      <div className="mb-4 flex flex-wrap items-center gap-1 border-b border-slate-200 dark:border-slate-700">
        <TabBtn active={tab === "list"} onClick={() => setTab("list")} testid="supp-tab-list">Tedarikçi Listesi</TabBtn>
        <TabBtn active={tab === "quality"} onClick={() => setTab("quality")} testid="supp-tab-quality">Kalite Performansı</TabBtn>
      </div>
      {tab === "list" ? <SuppliersListTab/> : <SupplierQualityTab/>}
    </div>
  );
}

function TabBtn({ active, onClick, testid, children }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition ${active ? "border-amber-600 text-amber-700 dark:text-amber-300" : "border-transparent text-slate-500 hover:text-slate-800 dark:hover:text-slate-200"}`}>
      {children}
    </button>
  );
}

function SuppliersListTab() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const empty = { name: "", contact_name: "", phone: "", email: "", tax_id: "", address: "", notes: "", active: true, payment_term_days: "" };
  const [f, setF] = useState(empty);
  const [tgOpen, setTgOpen] = useState(false);
  const [tgSupplier, setTgSupplier] = useState(null);
  const [tgCode, setTgCode] = useState(null);
  const [tgBusy, setTgBusy] = useState(false);
  const load = () => api.get("/suppliers").then(setRows);
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (!f.name.trim()) { alert("Tedarikçi adı zorunludur."); return; }
    // iter106: normalize payment_term_days — empty string → null, valid integer only
    const payload = { ...f };
    if (payload.payment_term_days === "" || payload.payment_term_days === null) {
      payload.payment_term_days = null;
    } else {
      const n = parseInt(payload.payment_term_days, 10);
      if (isNaN(n) || n < 0) { alert("Vade Gün geçerli bir tam sayı olmalı (0 veya pozitif)."); return; }
      payload.payment_term_days = n;
    }
    try {
      if (edit) await api.put(`/suppliers/${edit.id}`, payload);
      else await api.post("/suppliers", payload);
      setOpen(false); setEdit(null); setF(empty); load();
    } catch (e) { alert(e.response?.data?.detail || "Hata"); }
  };
  const openNew = () => { setEdit(null); setF(empty); setOpen(true); };
  const openEdit = (r) => { setEdit(r); setF({ ...empty, ...r }); setOpen(true); };
  const del = (r) => confirmAndDelete(`/suppliers/${r.id}`, r.name, load);

  const openTelegram = (r) => { setTgSupplier(r); setTgCode(null); setTgOpen(true); };
  const generateTgCode = async () => {
    if (!tgSupplier) return;
    setTgBusy(true);
    try {
      const res = await api.post("/telegram/link/supplier", { supplier_id: tgSupplier.id });
      setTgCode(res);
    } catch (e) { alert(e.response?.data?.detail || "Kod oluşturulamadı"); }
    finally { setTgBusy(false); }
  };
  const unlinkTelegram = async () => {
    if (!tgSupplier) return;
    if (!window.confirm(`${tgSupplier.name} Telegram bağlantısı kaldırılsın mı?`)) return;
    setTgBusy(true);
    try {
      await api.post("/telegram/unlink-supplier", { supplier_id: tgSupplier.id });
      setTgOpen(false); setTgSupplier(null); setTgCode(null);
      load();
    } catch (e) { alert(e.response?.data?.detail || "Bağlantı kaldırılamadı"); }
    finally { setTgBusy(false); }
  };
  const pg = usePagination(rows, "suppliers", 100);

  return (
    <div>
      <PageHeader title="Tedarikçiler" description="Hammadde ve ambalaj tedarikçileri" actions={
        <div className="flex items-center gap-2">
          <div className="inline-flex items-center gap-1 border rounded-md px-1.5 py-1 bg-slate-50" title="L16 Tedarikçi Listesi / L17 Onaylı Tedarikçi Listesi Kontrollü PDF">
            <span className="text-[10px] font-semibold text-slate-600 mr-1">L16:</span>
            <button title="Önizle" data-testid="supp-l16-preview"
              onClick={() => previewPdf("/api/suppliers/master-list/pdf", "L16_Tedarikci_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-indigo-600 hover:bg-indigo-700 text-white">
              <Eye size={14}/>
            </button>
            <button title="İndir" data-testid="supp-l16-download"
              onClick={() => downloadPdf("/api/suppliers/master-list/pdf", "L16_Tedarikci_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
              <Download size={14}/>
            </button>
            <button title="Yazdır" data-testid="supp-l16-print"
              onClick={() => printPdf("/api/suppliers/master-list/pdf", "L16_Tedarikci_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
              <Printer size={14}/>
            </button>
            <span className="text-[10px] font-semibold text-slate-600 mx-1">L17:</span>
            <button title="Onaylı Tedarikçi Önizle" data-testid="supp-l17-preview"
              onClick={() => previewPdf("/api/suppliers/approved-list/pdf", "L17_Onayli_Tedarikci_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-emerald-600 hover:bg-emerald-700 text-white">
              <Eye size={14}/>
            </button>
            <button title="Onaylı Tedarikçi İndir" data-testid="supp-l17-download"
              onClick={() => downloadPdf("/api/suppliers/approved-list/pdf", "L17_Onayli_Tedarikci_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
              <Download size={14}/>
            </button>
            <button title="Onaylı Tedarikçi Yazdır" data-testid="supp-l17-print"
              onClick={() => printPdf("/api/suppliers/approved-list/pdf", "L17_Onayli_Tedarikci_Listesi.pdf")}
              className="inline-flex items-center justify-center w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 border text-slate-800">
              <Printer size={14}/>
            </button>
          </div>
          <Btn onClick={openNew} data-testid="new-supplier"><Plus size={14}/> Yeni Tedarikçi</Btn>
        </div>
      } />
      <Table testid="suppliers-table" columns={[
        { label: "Kod", render: r => <span className="font-mono text-xs">{r.code}</span> },
        { label: "Firma", key: "name" },
        { label: "İlgili Kişi", key: "contact_name" },
        { label: "Telefon", key: "phone" },
        { label: "E-posta", key: "email" },
        { label: "Telegram", render: r => r.telegram_chat_id
          ? <Badge level="ok" testid={`supp-tg-linked-${r.id}`}>✓ Bağlı</Badge>
          : <span className="text-xs text-slate-400 italic" data-testid={`supp-tg-unlinked-${r.id}`}>Bağlı değil</span> },
        { label: "Durum", render: r => <Badge level={r.active ? "ok" : "inactive"}>{r.active ? "Aktif" : "Pasif"}</Badge> },
        { label: "İşlem", render: r => (
          <div className="flex gap-1 flex-wrap">
            <Btn size="sm" variant="secondary" onClick={() => openEdit(r)} data-testid={`edit-supp-${r.id}`}><Pencil size={12}/> Düzenle</Btn>
            <Btn size="sm" variant="secondary" onClick={() => openTelegram(r)} data-testid={`supp-tg-btn-${r.id}`}
              title={r.telegram_chat_id ? "Telegram bağlantısını yönet" : "Telegram Bağla"}>
              <Send size={12}/> {r.telegram_chat_id ? "Telegram ✓" : "Telegram Bağla"}
            </Btn>
            <Btn size="sm" variant="danger" onClick={() => del(r)} data-testid={`delete-supp-${r.id}`}><Trash2 size={12}/> Sil</Btn>
          </div>
        ) },
      ]} rows={pg.paged} />
      <ListPagination pagination={pg} testid="suppliers-pagination"/>
      <Modal open={tgOpen} onClose={() => setTgOpen(false)} title={`Telegram Bağla — ${tgSupplier?.name || ""}`} size="md">
        {tgSupplier?.telegram_chat_id ? (
          <div className="space-y-3" data-testid="supp-tg-linked-panel">
            <div className="p-3 bg-emerald-50 border border-emerald-200 rounded text-sm">
              <div className="flex items-center gap-2 font-semibold text-emerald-800 mb-1">✓ Bu tedarikçi Telegram'a bağlı</div>
              <div className="text-xs text-slate-600">Chat ID: <code className="font-mono">{tgSupplier.telegram_chat_id}</code></div>
              <div className="text-xs text-slate-500 mt-1">Satın alma siparişi PDF'leri Telegram üzerinden bu chat'e gönderilebilir.</div>
            </div>
            <div className="flex justify-end gap-2">
              <Btn variant="secondary" onClick={() => setTgOpen(false)}>Kapat</Btn>
              <Btn variant="danger" onClick={unlinkTelegram} disabled={tgBusy} data-testid="supp-tg-unlink">Bağlantıyı Kaldır</Btn>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="supp-tg-unlinked-panel">
            <div className="p-3 bg-sky-50 border border-sky-200 rounded text-xs text-slate-700 space-y-2">
              <div className="font-semibold text-sky-900">Bağlama Adımları</div>
              <ol className="list-decimal ml-4 space-y-1">
                <li>"Kod Oluştur" butonuna basın — 6 haneli tek kullanımlık bir kod üretilir.</li>
                <li>Kodu tedarikçiye iletin (WhatsApp, e-posta vb.).</li>
                <li>Tedarikçi Telegram'da <b>@Metapharmaerpbot</b> botuna <code>/link KOD</code> yazarak bağlansın.</li>
                <li>Bağlantı sonrası PO PDF'lerini Telegram ile gönderebilirsiniz.</li>
              </ol>
            </div>
            <div className="flex justify-center">
              <Btn onClick={generateTgCode} disabled={tgBusy} data-testid="supp-tg-generate">
                <KeyRound size={12}/> Kod Oluştur
              </Btn>
            </div>
            {tgCode && (
              <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-sm" data-testid="supp-tg-code-panel">
                <div className="font-mono text-2xl font-bold text-emerald-700 tracking-widest text-center py-1" data-testid="supp-tg-code">{tgCode.code}</div>
                {tgCode.deep_link && (
                  <a href={tgCode.deep_link} target="_blank" rel="noreferrer" className="block text-center text-xs text-sky-700 hover:underline mt-1" data-testid="supp-tg-deep-link">
                    → Telegram'da bağla (tek tıkla)
                  </a>
                )}
                <div className="text-[10px] text-slate-500 text-center mt-1">
                  Kod 1 kez kullanılır. Tedarikçi Telegram'a: <code>/link {tgCode.code}</code>
                </div>
              </div>
            )}
            <div className="flex justify-end">
              <Btn variant="secondary" onClick={() => setTgOpen(false)}>Kapat</Btn>
            </div>
          </div>
        )}
      </Modal>
      <Modal open={open} onClose={() => setOpen(false)} title={edit ? "Tedarikçi Düzenle" : "Yeni Tedarikçi"} size="lg">
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Input label="Firma Adı *" value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid="supp-name" /></div>
          <Input label="İlgili Kişi" value={f.contact_name} onChange={e => setF({ ...f, contact_name: e.target.value })} data-testid="supp-contact" />
          <Input label="Telefon" value={f.phone} onChange={e => setF({ ...f, phone: e.target.value })} data-testid="supp-phone" />
          <Input label="E-posta" value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid="supp-email" />
          <Input label="Vergi No" value={f.tax_id} onChange={e => setF({ ...f, tax_id: e.target.value })} />
          <Input label="Vade Gün (gün)" type="number" min="0" placeholder="Örn. 30, 60, 90" value={f.payment_term_days ?? ""} onChange={e => setF({ ...f, payment_term_days: e.target.value })} data-testid="supp-payment-term-days" />
          <div className="col-span-2 text-[11px] text-slate-500 italic -mt-1">Vade Gün: Alım tarihi + Vade Gün otomatik Vade Tarihi hesaplar. Boş bırakılırsa manuel Vade Tarihi kullanılır.</div>
          <div className="col-span-2"><Textarea label="Adres" value={f.address} onChange={e => setF({ ...f, address: e.target.value })} /></div>
          <div className="col-span-2"><Textarea label="Notlar" value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} /></div>
          <Select label="Durum" value={f.active ? "1" : "0"} onChange={e => setF({ ...f, active: e.target.value === "1" })}>
            <option value="1">Aktif</option><option value="0">Pasif</option>
          </Select>
        </div>
        <div className="text-xs text-slate-500 mt-3 italic">Not: Sadece "Firma Adı" zorunludur. Diğer alanlar sonradan doldurulabilir.</div>
        <div className="flex justify-end gap-2 mt-4"><Btn variant="secondary" onClick={() => setOpen(false)}>Vazgeç</Btn><Btn onClick={save} data-testid="save-supplier">Kaydet</Btn></div>
      </Modal>
    </div>
  );
}

/* ---------- Supplier Quality Performance (Prompt 3 Task 2) ---------- */
function SupplierQualityTab() {
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);
  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/suppliers/quality-list?months=${months}`);
      setData(r);
    } catch (e) { setData({ suppliers: [] }); }
    finally { setBusy(false); }
  }, [months]);
  React.useEffect(() => { load(); }, [load]);

  const gradeColor = (grade) => {
    if (!grade) return "inactive";
    if (grade.startsWith("A")) return "ok";
    if (grade.startsWith("B")) return "ok";
    if (grade.startsWith("C")) return "attention";
    if (grade.startsWith("D") || grade.startsWith("F")) return "missing";
    return "inactive";
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1.5 text-xs">
          <span className="text-slate-500 uppercase tracking-wider font-medium">Dönem:</span>
          {[3, 6, 12].map(m => (
            <button key={m} onClick={() => setMonths(m)} data-testid={`sq-months-${m}`}
              className={`px-2.5 py-1 rounded-md border text-xs font-medium transition ${months === m ? "bg-amber-100 dark:bg-amber-500/20 border-amber-400 text-amber-800 dark:text-amber-200" : "bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 hover:border-amber-300"}`}>
              Son {m} Ay
            </button>
          ))}
        </div>
        <button onClick={load} className="text-xs text-slate-500 hover:text-slate-800">Yenile</button>
      </div>
      <div className="rounded-md bg-sky-50 dark:bg-sky-500/10 border border-sky-200 dark:border-sky-500/30 px-3 py-2 text-xs text-sky-800 dark:text-sky-200">
        Skorlar mevcut Satın Alma, Teslim Alma, QC ve İade verilerinden <b>dinamik hesaplanır</b>. Yeni veri girişi yapılmaz.
      </div>
      <Card className="p-0 overflow-hidden">
        <Table testid="supplier-quality-table"
          columns={[
            { label: "Firma", render: r => <span className="font-medium">{r.supplier_name}</span> },
            { label: "Skor", render: r => r.score !== null ? (
              <span className="tabular-nums font-bold text-lg">{r.score}</span>
            ) : <span className="text-slate-400 text-xs italic">veri yok</span> },
            { label: "Not", render: r => <Badge level={gradeColor(r.grade)}>{r.grade}</Badge> },
            { label: "Zamanında %", render: r => r.on_time_pct !== null ? <span className="tabular-nums">{r.on_time_pct}%</span> : "—" },
            { label: "QC Geçme %", render: r => r.qc_pass_pct !== null ? <span className="tabular-nums">{r.qc_pass_pct}%</span> : "—" },
            { label: "İade Sayısı", render: r => <span className="tabular-nums">{r.returns_count}</span> },
            { label: "İşlem", render: r => (
              <Btn size="sm" variant="secondary" onClick={() => setOpenDetail(r)} data-testid={`sq-detail-${r.supplier_id}`}>Detay</Btn>
            )},
          ]}
          rows={data?.suppliers || []}
          empty={busy ? "Yükleniyor..." : "Aktif tedarikçi yok"}/>
      </Card>
      {openDetail && (
        <Modal open={true} onClose={() => setOpenDetail(null)} title={`Kalite Performansı — ${openDetail.supplier_name}`} size="xl">
          <SupplierQualityDetail supplierId={openDetail.supplier_id}/>
        </Modal>
      )}
    </div>
  );
}

function SupplierQualityDetail({ supplierId }) {
  const [doc, setDoc] = React.useState(null);
  React.useEffect(() => {
    api.get(`/suppliers/${supplierId}/quality-score`).then(setDoc).catch(() => setDoc(null));
  }, [supplierId]);
  if (!doc) return <div className="text-center py-8 text-slate-400">Yükleniyor...</div>;
  return (
    <div className="space-y-4">
      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <div className="text-[10px] uppercase text-slate-500">Firma</div>
          <div className="font-semibold">{doc.supplier?.name}</div>
        </div>
        <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <div className="text-[10px] uppercase text-slate-500">Kod</div>
          <div className="font-mono">{doc.supplier?.code || "—"}</div>
        </div>
        <div className="p-2 rounded-md bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
          <div className="text-[10px] uppercase text-slate-500">Durum</div>
          <div>{doc.supplier?.active ? <Badge level="ok">Aktif</Badge> : <Badge level="inactive">Pasif</Badge>}</div>
        </div>
      </div>

      {/* Trend */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Trend (3 / 6 / 12 ay)</div>
        <div className="grid md:grid-cols-3 gap-3">
          {(doc.trends || []).map(t => (
            <div key={t.months} data-testid={`sq-trend-${t.months}`}
              className="p-3 rounded-lg border border-slate-200 dark:border-slate-700 bg-white/70 dark:bg-slate-800/40">
              <div className="text-[10px] uppercase text-slate-500 tracking-wider">Son {t.months} Ay</div>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-3xl font-bold tabular-nums">{t.score?.score ?? "—"}</span>
                <span className="text-xs text-slate-500">/100</span>
              </div>
              <div className="mt-1 text-xs">{t.score?.grade || "—"}</div>
              <div className="mt-2 space-y-1 text-[11px]">
                <div className="flex justify-between"><span className="text-slate-500">Zamanında</span><span className="tabular-nums font-medium">{t.metrics?.on_time_pct ?? "—"}% <span className="text-slate-400">(A×40)</span></span></div>
                <div className="flex justify-between"><span className="text-slate-500">QC Geçme</span><span className="tabular-nums font-medium">{t.metrics?.qc_pass_pct ?? "—"}% <span className="text-slate-400">(A×35)</span></span></div>
                <div className="flex justify-between"><span className="text-slate-500">İade Sayısı</span><span className="tabular-nums font-medium">{t.metrics?.returns_count}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">PO Sayısı</span><span className="tabular-nums font-medium">{t.metrics?.po_total}</span></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Late deliveries */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Son Geciken Teslimatlar (12 ay)</div>
        <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto bg-white/70 dark:bg-slate-800/40">
          {(doc.late_deliveries || []).length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-4">Gecikmeli teslimat yok</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60"><tr>
                <th className="text-left px-2 py-1.5 font-semibold">PO No</th>
                <th className="text-left px-2 py-1.5 font-semibold">Beklenen</th>
                <th className="text-left px-2 py-1.5 font-semibold">Gerçek</th>
                <th className="text-right px-2 py-1.5 font-semibold">Gecikme</th>
              </tr></thead>
              <tbody>
                {doc.late_deliveries.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-2 py-1 font-mono">{l.po_no}</td>
                    <td className="px-2 py-1">{l.expected}</td>
                    <td className="px-2 py-1">{(l.received || "").slice(0, 10)}</td>
                    <td className="px-2 py-1 text-right font-semibold text-rose-600">{l.delay_days} gün</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Rejected lots */}
      <div>
        <div className="text-xs font-semibold uppercase tracking-wider text-slate-600 dark:text-slate-300 mb-2">Reddedilen Lotlar (12 ay)</div>
        <div className="border border-slate-200 dark:border-slate-700 rounded-md overflow-x-auto bg-white/70 dark:bg-slate-800/40">
          {(doc.rejected_lots || []).length === 0 ? (
            <div className="text-xs text-slate-400 text-center py-4">Reddedilen lot yok</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-slate-50 dark:bg-slate-800/60"><tr>
                <th className="text-left px-2 py-1.5 font-semibold">Lot</th>
                <th className="text-left px-2 py-1.5 font-semibold">Malzeme</th>
                <th className="text-right px-2 py-1.5 font-semibold">Miktar</th>
                <th className="text-left px-2 py-1.5 font-semibold">Tarih</th>
              </tr></thead>
              <tbody>
                {doc.rejected_lots.map((l, i) => (
                  <tr key={i} className="border-t border-slate-100 dark:border-slate-700">
                    <td className="px-2 py-1 font-mono">{l.lot_code}</td>
                    <td className="px-2 py-1">{l.material_name || "—"}</td>
                    <td className="px-2 py-1 text-right tabular-nums">{l.qty} {l.unit || ""}</td>
                    <td className="px-2 py-1">{(l.created_at || "").slice(0, 10)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
